import { useCallback, useEffect, useRef, useState } from "react";
import { AndroidExpertPanel } from "./AndroidExpertPanel";
import { ApiError, api, post } from "./api";
import { EvolutionPlanner } from "./EvolutionPlanner";
import { type EmulatorHandle, EmulatorView, type StreamBackend } from "./emulator/EmulatorView";
import { type StreamProfile } from "./emulator/AndroidWebRtcClient";
import { directControl } from "./emulator/controlStore";
import type { TeslaAudioState } from "./emulator/TeslaAndroidAudioClient";
import { PerformanceHud } from "./performance/PerformanceHud";
import { performanceStore, usePerformanceSnapshot } from "./performance/store";
import { startPlaybackTelemetry } from "./performance/telemetry";
import { TeslaAndroidSettingsDialog } from "./TeslaAndroidSettings";
import type { AgentAction, AgentEvent, AgentSchedule, AgentTask, AiHealth, AppInfo, CodexModels, DeviceStatus, EvolutionPlan, GamePlan, GameScheduleDraft, Message, PlaybackTelemetrySession, RuntimeList, ScheduleRun, TeslaVirtualDisplayRestartResponse } from "./types";

type Tab = "agent" | "schedules" | "reports" | "expert" | "apps" | "diagnostics";
type ControlMode = "human" | "ai" | "shared";
interface StreamConfig {
  defaultBackend: StreamBackend;
  defaultProfile?: StreamProfile;
  backends: StreamBackend[];
  androidRuntime: string;
  performanceProfile: string;
  configuredFps: number;
}

const terminal = new Set(["completed", "failed", "cancelled"]);
const observationTools = new Set([
  "android_status", "android_screenshot", "android_ui_tree", "android_current_app",
  "android_list_apps", "android_app_info", "android_wait", "android_wait_for_new_app",
  "android_wait_for_app_update",
]);
const defaultTaskMaxActions = 100;
const defaultTaskTimeoutSeconds = 900;

function Icon({ children }: { children: string }) {
  return <span aria-hidden="true" className="icon">{children}</span>;
}

function RuntimeStatusPanel({ device, config, backend }: {
  device?: DeviceStatus["device"];
  config: StreamConfig;
  backend: StreamBackend;
}) {
  const stream = usePerformanceSnapshot();
  const degraded = device?.performanceState?.includes("degraded");
  return <div className={`runtime-strip ${degraded ? "degraded" : "hardware"}`}>
    <span><small>Runtime</small><strong>{device?.runtime || config.androidRuntime}</strong></span>
    <span><small>Android / device</small><strong>{device?.androidVersion || `API ${device?.apiLevel || "?"}`} · {device?.model || (device?.runtimeKind === "physical" ? "Physical" : "Emulator")}</strong></span>
    <span><small>Graphics</small><strong>{device?.graphicsRenderer || "Detecting"}</strong></span>
    <span><small>Performance mode</small><strong>{device?.performanceProfile || config.performanceProfile}</strong></span>
    <span><small>Encoder</small><strong>{stream.senderEncoder || "Detecting"} · {stream.senderEncoderType || "Unknown"}</strong></span>
    <span><small>Decoder</small><strong>{stream.decoderImplementation || "Detecting"}</strong></span>
    <span><small>Stream</small><strong>{backend} · {stream.width || device?.screen?.width || "?"}×{stream.height || device?.screen?.height || "?"} · {stream.fps ?? 0}/{device?.configuredFps || config.configuredFps} FPS</strong></span>
    <span><small>Input / connection</small><strong>{stream.touchToVisibleMs === undefined ? "Unmeasured" : `${stream.touchToVisibleMs} ms`} · {device?.connectionType || "Unknown"}</strong></span>
    {degraded && <em>Software fallback</em>}
  </div>;
}

function App() {
  const screenShell = useRef<HTMLDivElement>(null);
  const deviceFrame = useRef<HTMLDivElement>(null);
  const emulator = useRef<EmulatorHandle>(null);
  const [tab, setTab] = useState<Tab>(() =>
    window.location.hash === "#schedules-report" ? "reports" : "agent"
  );
  const [streamState, setStreamState] = useState("connecting");
  const [streamError, setStreamError] = useState<string>();
  const [dimensions, setDimensions] = useState({ width: 960, height: 540 });
  const [status, setStatus] = useState<DeviceStatus>();
  const [aiHealth, setAiHealth] = useState<AiHealth>();
  const [codexModels, setCodexModels] = useState<CodexModels>();
  const [modelBusy, setModelBusy] = useState(false);
  const [foreground, setForeground] = useState("Unknown");
  const [controlMode, setControlMode] = useState<ControlMode>("human");
  const [sessionId, setSessionId] = useState<string>();
  const [task, setTask] = useState<AgentTask>();
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [actions, setActions] = useState<AgentAction[]>([]);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [modelNotice, setModelNotice] = useState<string>();
  const [schedules, setSchedules] = useState<AgentSchedule[]>([]);
  const [scheduleRuns, setScheduleRuns] = useState<ScheduleRun[]>([]);
  const [scheduleReports, setScheduleReports] = useState<ScheduleRun[]>([]);
  const scheduleStatuses = useRef<Map<number, string>>(new Map());
  const schedulesInitialized = useRef(false);
  const [streamProfile, setStreamProfile] = useState<StreamProfile>("balanced");
  const [streamBackend, setStreamBackend] = useState<StreamBackend>("scrcpy-webcodecs");
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [audioState, setAudioState] = useState<TeslaAudioState>("off");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [streamConfigLoaded, setStreamConfigLoaded] = useState(false);
  const [streamConfig, setStreamConfig] = useState<StreamConfig>({
    defaultBackend: "scrcpy-webcodecs",
    backends: ["native-webrtc", "scrcpy-webcodecs"],
    androidRuntime: "google-emulator-api36",
    performanceProfile: "software-balanced",
    configuredFps: 30,
  });
  const [runtimeList, setRuntimeList] = useState<RuntimeList>({ active: "google-emulator-api36", runtimes: [] });
  const [runtimeBusy, setRuntimeBusy] = useState(false);
  const [teslaSettingsOpen, setTeslaSettingsOpen] = useState(false);

  useEffect(() => startPlaybackTelemetry(), []);
  const selectTab = useCallback((next: Tab) => {
    setTab(next);
    if (next === "reports") {
      window.history.replaceState(null, "", "#schedules-report");
    } else if (window.location.hash === "#schedules-report") {
      window.history.replaceState(
        null, "", `${window.location.pathname}${window.location.search}`
      );
    }
  }, []);

  useEffect(() => {
    const syncHash = () => {
      if (window.location.hash === "#schedules-report") setTab("reports");
    };
    window.addEventListener("hashchange", syncHash);
    return () => window.removeEventListener("hashchange", syncHash);
  }, []);
  const handleStreamError = useCallback((message: string) => {
    setStreamError(message || undefined);
    if (streamBackend === "native-webrtc" &&
        streamConfig.backends.includes("scrcpy-webcodecs") &&
        ("VideoDecoder" in window || "MediaSource" in window)) {
      setNotice("Native WebRTC could not deliver video; switched to the scrcpy H.264 stream.");
      setStreamBackend("scrcpy-webcodecs");
    }
  }, [streamBackend, streamConfig.backends]);

  const refreshStatus = useCallback(async () => {
    try {
      const [health, agentHealth, control] = await Promise.all([
        api<DeviceStatus>("/api/frontend-status"),
        api<AiHealth>("/agent/health"),
        api<{ mode: ControlMode }>("/agent/control"),
      ]);
      setStatus(health);
      setAiHealth(agentHealth);
      setForeground(health.foreground?.packageName || "Android system");
      const relay = health.services?.scrcpyGateway?.teslaRelay?.channels;
      performanceStore.update({
        foregroundPackage: health.foreground?.packageName || "Android system",
        relayDisplayReconnects: relay?.display?.upstreamReconnects,
        relayAudioReconnects: relay?.audio?.upstreamReconnects,
        relayDisplayGapCount: relay?.display?.upstreamGapCount,
        relayAudioGapCount: relay?.audio?.upstreamGapCount,
        relayDisplayMaxGapMs: relay?.display?.maxUpstreamGapMs,
        relayAudioMaxGapMs: relay?.audio?.maxUpstreamGapMs,
        relayDisplaySlowSends: relay?.display?.downstreamSlowSends,
        relayAudioSlowSends: relay?.audio?.downstreamSlowSends,
        relayDisplayMaxSendWaitMs: relay?.display?.downstreamSendWaitMsMax,
        relayAudioMaxSendWaitMs: relay?.audio?.downstreamSendWaitMsMax,
      });
      directControl.setMode(control.mode);
      setControlMode(control.mode);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    }
  }, []);

  useEffect(() => {
    refreshStatus();
    const timer = window.setInterval(refreshStatus, 10_000);
    return () => window.clearInterval(timer);
  }, [refreshStatus]);

  const refreshRuntimes = useCallback(async () => {
    try {
      const result = await api<RuntimeList>("/api/runtimes");
      setRuntimeList(result);
      if (result.active === "remote-waydroid") setStreamBackend("remote-waydroid");
      if (result.active === "tesla-android-cm4") setStreamBackend("tesla-native");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    }
  }, []);

  useEffect(() => { void refreshRuntimes(); }, [refreshRuntimes]);

  const refreshModels = useCallback(async () => {
    try {
      setCodexModels(await api<CodexModels>("/agent/models"));
      setModelNotice(undefined);
    } catch (error) {
      setModelNotice(error instanceof ApiError && error.status === 503
        ? "AI model selection is temporarily unavailable. Retrying automatically…"
        : `AI model list unavailable: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, []);

  useEffect(() => {
    void refreshModels();
    const timer = window.setInterval(() => void refreshModels(), 15_000);
    return () => window.clearInterval(timer);
  }, [refreshModels]);

  const refreshSchedules = useCallback(async () => {
    try {
      const [scheduleResult, runResult, reportResult] = await Promise.all([
        api<{ schedules: AgentSchedule[] }>("/agent/schedules"),
        api<{ runs: ScheduleRun[] }>("/agent/schedule-runs?limit=100"),
        api<{ reports: ScheduleRun[] }>("/agent/schedule-reports?limit=200"),
      ]);
      setSchedules(scheduleResult.schedules);
      setScheduleRuns(runResult.runs);
      setScheduleReports(reportResult.reports);
      const previous = scheduleStatuses.current;
      if (schedulesInitialized.current) {
        const completed = runResult.runs.filter((run) =>
          terminal.has(run.status) && !terminal.has(previous.get(run.id) || "")
        );
        const latest = completed.at(-1);
        if (latest) {
          const schedule = scheduleResult.schedules.find((item) => item.id === latest.schedule_id);
          const label = schedule ? scheduleDisplayName(schedule) : latest.schedule_name;
          setNotice(`Scheduled task “${label}” ${latest.status}: ${latest.message || "finished"}`);
        }
      }
      scheduleStatuses.current = new Map(runResult.runs.map((run) => [run.id, run.status]));
      schedulesInitialized.current = true;
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    }
  }, []);

  useEffect(() => {
    void refreshSchedules();
    const timer = window.setInterval(() => void refreshSchedules(), 5_000);
    return () => window.clearInterval(timer);
  }, [refreshSchedules]);

  useEffect(() => {
    api<StreamConfig>("/stream-config")
      .then((config) => {
        setStreamConfig(config);
        setStreamBackend(config.defaultBackend);
        if (["ultra-low-latency", "balanced", "quality"].includes(config.defaultProfile || "")) {
          setStreamProfile(config.defaultProfile as StreamProfile);
        }
      })
      .catch(() => { /* The compiled emulator defaults remain available. */ })
      .finally(() => setStreamConfigLoaded(true));
  }, []);

  const selectModel = async (model: string, role: "agent" | "vision" | "expert") => {
    setModelBusy(true);
    try {
      setCodexModels(await post<CodexModels>("/agent/models", { model, role }));
      await refreshStatus();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setModelBusy(false);
    }
  };

  const selectRuntime = async (selected: string) => {
    if (selected === runtimeList.active) return;
    setRuntimeBusy(true);
    setNotice(undefined);
    try {
      if (task && !terminal.has(task.status)) {
        throw new Error("Pause or finish the active AI task before changing Android runtime");
      }
      await setMode("human");
      const result = await post<RuntimeList>("/api/runtimes/select", { runtime: selected });
      setRuntimeList(result);
      setStreamBackend(selected === "remote-waydroid"
        ? "remote-waydroid"
        : selected === "tesla-android-cm4" ? "tesla-native" : "scrcpy-webcodecs");
      setSessionId(undefined);
      setMessages([]); setEvents([]); setActions([]); setTask(undefined);
      await refreshStatus();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setRuntimeBusy(false);
    }
  };

  useEffect(() => {
    const node = screenShell.current;
    if (!node) return;
    const resize = () => {
      if (document.fullscreenElement === node) {
        setDimensions({ width: Math.max(1, node.clientWidth), height: Math.max(1, node.clientHeight) });
        return;
      }
      const pixels = (value: string) => Number.parseFloat(value) || 0;
      const shellStyle = window.getComputedStyle(node);
      const frameStyle = deviceFrame.current ? window.getComputedStyle(deviceFrame.current) : undefined;
      const horizontalChrome = pixels(shellStyle.paddingLeft) + pixels(shellStyle.paddingRight)
        + (frameStyle ? pixels(frameStyle.paddingLeft) + pixels(frameStyle.paddingRight)
          + pixels(frameStyle.borderLeftWidth) + pixels(frameStyle.borderRightWidth) : 0);
      const verticalChrome = pixels(shellStyle.paddingTop) + pixels(shellStyle.paddingBottom)
        + (frameStyle ? pixels(frameStyle.paddingTop) + pixels(frameStyle.paddingBottom)
          + pixels(frameStyle.borderTopWidth) + pixels(frameStyle.borderBottomWidth) : 0);
      // Dimensions describe the input surface, not the decorated device frame.
      // Account for both boxes so overflow:hidden never clips the bottom/right
      // touch rows while preserving the Android display aspect ratio.
      const availableWidth = Math.max(1, node.clientWidth - horizontalChrome);
      const availableHeight = Math.max(1, node.clientHeight - verticalChrome);
      const screenWidth = status?.device?.screen?.width || 1280;
      const screenHeight = status?.device?.screen?.height || 720;
      const scale = Math.min(availableWidth / screenWidth, availableHeight / screenHeight);
      setDimensions({ width: Math.floor(screenWidth * scale), height: Math.floor(screenHeight * scale) });
    };
    const observer = new ResizeObserver(resize);
    observer.observe(node);
    resize();
    return () => observer.disconnect();
  }, [isFullscreen, status?.device?.screen?.width, status?.device?.screen?.height]);

  useEffect(() => {
    const syncFullscreenState = () => setIsFullscreen(document.fullscreenElement === screenShell.current);
    document.addEventListener("fullscreenchange", syncFullscreenState);
    syncFullscreenState();
    return () => document.removeEventListener("fullscreenchange", syncFullscreenState);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement === screenShell.current) await document.exitFullscreen();
      else await screenShell.current?.requestFullscreen();
    } catch (error) {
      setNotice(`Fullscreen: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  useEffect(() => {
    if (!task) return;
    const source = new EventSource(`/agent/tasks/${task.id}/events`);
    const receive = (event: MessageEvent) => {
      const item = JSON.parse(event.data) as AgentEvent;
      setEvents((current) => current.some((existing) => existing.id === item.id) ? current : [...current, item]);
      if (["completed", "failed", "cancelled", "paused", "resumed"].includes(item.type)) refreshTask(task.id);
      if (terminal.has(item.type)) source.close();
    };
    ["thinking", "observing", "waiting-external", "action-planned", "action-finished", "instruction", "paused", "resumed", "completed", "failed", "cancelled"].forEach((name) => source.addEventListener(name, receive));
    source.onerror = () => { if (task.status && terminal.has(task.status)) source.close(); };
    const poll = window.setInterval(() => refreshTask(task.id), 2_500);
    return () => { source.close(); window.clearInterval(poll); };
  }, [task?.id]);

  const refreshTask = async (taskId: string) => {
    try {
      const next = await api<AgentTask>(`/agent/tasks/${taskId}`);
      setTask(next);
      setActions(next.actions || []);
      if (next.session_id) {
        const history = await api<{ messages: Message[] }>(`/agent/sessions/${next.session_id}/messages`);
        setMessages(history.messages);
      }
    } catch { /* transient polling errors are reflected by diagnostics */ }
  };

  const setMode = async (mode: ControlMode) => {
    const result = await post<{ mode: ControlMode }>("/agent/control", { mode });
    directControl.setMode(result.mode);
    setControlMode(result.mode);
  };

  const manualAction = async (path: string, body: unknown = {}) => {
    try {
      await post(`/api/${path}`, body);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    }
  };

  const submitInstruction = async () => {
    const text = instruction.trim();
    if (!text) return;
    setBusy(true);
    setNotice(undefined);
    try {
      if (task && !terminal.has(task.status)) {
        await post(`/agent/tasks/${task.id}/instructions`, { instruction: text });
        setMessages((current) => [...current, { id: Date.now(), role: "user", content: text, created_at: Date.now() / 1000 }]);
      } else {
        await setMode("ai");
        let activeSession = sessionId;
        if (!activeSession) {
          const session = await post<{ id: string }>("/agent/sessions", { taskDescription: text });
          activeSession = session.id;
          setSessionId(session.id);
        }
        const created = await post<AgentTask>("/agent/tasks", {
          instruction: text,
          sessionId: activeSession,
          maxActions: defaultTaskMaxActions,
          timeoutSeconds: defaultTaskTimeoutSeconds,
        });
        setTask(created);
        setEvents([]);
        setActions([]);
      }
      setInstruction("");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const taskCommand = async (command: "pause" | "resume" | "cancel") => {
    if (!task) return;
    try {
      if (command === "pause") await setMode("human");
      if (command === "resume") await setMode("ai");
      const next = await post<AgentTask>(`/agent/tasks/${task.id}/${command}`);
      setTask(next);
    } catch (error) { setNotice(error instanceof Error ? error.message : String(error)); }
  };

  const newSession = async () => {
    if (task && !terminal.has(task.status)) await post(`/agent/tasks/${task.id}/cancel`);
    const session = await post<{ id: string }>("/agent/sessions", {});
    setSessionId(session.id);
    setTask(undefined);
    setMessages([]);
    setEvents([]);
    setActions([]);
  };

  const downloadScreenshot = async () => {
    const response = await fetch("/api/screenshot");
    if (!response.ok) return setNotice("Screenshot failed");
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = `is4f-android-${new Date().toISOString().replaceAll(":", "-")}.png`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleAudio = () => {
    const enabled = audioState === "blocked" ? true : !audioEnabled;
    // Call through the mounted client inside this click event so Chromium's
    // user-activation requirement is satisfied before React runs effects.
    emulator.current?.setAudioEnabled(enabled);
    setAudioEnabled(enabled);
    if (!enabled) setAudioState("off");
  };

  const signOut = async () => {
    try {
      await fetch("/auth/logout", { method: "POST", headers: { Accept: "application/json" } });
    } finally {
      window.location.replace("/login");
    }
  };

  const connectionGood = status?.device?.connected && status?.device?.booted;
  const remoteRuntime = runtimeList.active === "remote-waydroid" || status?.device?.runtime === "remote-waydroid";
  const teslaRuntime = runtimeList.active === "tesla-android-cm4" || status?.device?.runtime === "tesla-android-cm4";
  const hardwareRuntime = remoteRuntime || teslaRuntime || status?.device?.runtimeKind === "physical" || ["physical-android", "tesla-android-cm4"].includes(streamConfig.androidRuntime);
  const manualEnabled = controlMode === "human" || controlMode === "shared";

  useEffect(() => {
    setStreamError(undefined);
    setStreamState("connecting");
    performanceStore.update({
      backend: streamBackend,
      reconnectCount: 0,
      codec: undefined,
      senderCodec: undefined,
      senderEncoder: undefined,
      decoderImplementation: undefined,
      capturedFps: undefined,
      encodedFps: undefined,
      streamQueueDepth: undefined,
      streamGatewayDrops: undefined,
      connectionMode: undefined,
      framesDecoded: undefined,
      framesDropped: undefined,
      fps: undefined,
      icePath: undefined,
      protocol: undefined,
    });
  }, [streamBackend]);

  useEffect(() => {
    if (streamBackend === "tesla-native") return;
    emulator.current?.setAudioEnabled(false);
    setAudioEnabled(false);
    setAudioState("off");
  }, [streamBackend]);

  useEffect(() => {
    performanceStore.update({
      androidRuntime: status?.device?.runtime || streamConfig.androidRuntime,
      androidRenderer: status?.device?.graphicsRenderer || "Unknown",
      configuredFps: status?.device?.configuredFps || streamConfig.configuredFps,
    });
  }, [status?.device?.graphicsRenderer, status?.device?.configuredFps, status?.device?.runtime, streamConfig.androidRuntime, streamConfig.configuredFps]);

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand"><span className="brand-mark">I4</span><div><strong>IS4F</strong><small>Android Emulator</small></div></div>
        <div className="top-status">
          <span className={`dot ${connectionGood ? "good" : "bad"}`} />
          <span>{connectionGood ? "Android ready" : "Android unavailable"}</span>
          <span className="divider" />
          <span className={`dot ${aiHealth?.ai?.connected ? "good" : "bad"}`} />
          <span>{aiHealth?.ai?.connected ? `Codex · ${aiHealth.ai.model || "account default"}` : "Codex unavailable"}</span>
          <span className="divider" />
          <span className={`mode mode-${controlMode}`}>{controlMode === "human" ? "Human control" : controlMode === "ai" ? "AI control" : "Shared"}</span>
        </div>
        <div className="model-pickers">
          <label className="model-picker runtime-picker">
            <span>Runtime</span>
            <select title="Active Android runtime" value={runtimeList.active} disabled={runtimeBusy || runtimeList.runtimes.length < 2} onChange={(event) => void selectRuntime(event.target.value)}>
              {runtimeList.runtimes.map((item) => <option key={item.id} value={item.id} disabled={!item.available && item.id !== runtimeList.active}>{item.label}{item.available ? "" : " · offline"}</option>)}
            </select>
          </label>
          <label className="model-picker profile-picker">
            <span>Backend</span>
            <select title="Streaming backend" value={streamBackend} onChange={(event) => setStreamBackend(event.target.value as StreamBackend)}>
              {remoteRuntime ? <option value="remote-waydroid">Pi WDR1 · H.264</option> : teslaRuntime ? <>
                <option value="tesla-native" disabled={!streamConfig.backends.includes("tesla-native")}>TeslaAndroid native · H.264</option>
                <option value="scrcpy-webcodecs" disabled={!streamConfig.backends.includes("scrcpy-webcodecs")}>scrcpy backup · H.264</option>
              </> : <>
                <option value="native-webrtc" disabled={!streamConfig.backends.includes("native-webrtc")}>Native WebRTC</option>
                <option value="scrcpy-webcodecs" disabled={!streamConfig.backends.includes("scrcpy-webcodecs")}>scrcpy · H.264</option>
              </>}
            </select>
          </label>
          <label className="model-picker profile-picker">
            <span>Stream</span>
            <select title="Streaming performance profile" value={streamProfile} onChange={(event) => setStreamProfile(event.target.value as StreamProfile)}>
              <option value="ultra-low-latency">{hardwareRuntime ? "Hardware" : "Software"} low latency · {streamConfig.configuredFps} FPS</option>
              <option value="balanced">{hardwareRuntime ? "Hardware" : "Software"} balanced · {streamConfig.configuredFps} FPS</option>
              {!hardwareRuntime && <option value="quality">Software compatibility · {streamConfig.configuredFps} FPS</option>}
            </select>
          </label>
          <label className="model-picker">
            <span>Vision</span>
            <select data-role="vision" title="Screen recognition model" value={codexModels?.visionSelected || ""} disabled={modelBusy || !codexModels?.models.length} onChange={(event) => selectModel(event.target.value, "vision")}>
              {!codexModels?.models.length && <option value="">Unavailable</option>}
              {codexModels?.models.map((model) => <option key={model.id} value={model.id}>{model.label}{model.isDefault ? " · default" : ""}</option>)}
            </select>
          </label>
          <span className="model-handoff" title="The vision model hands its screen map to the action model">→</span>
          <label className="model-picker">
            <span>Action</span>
            <select data-role="agent" title="Android action model" value={codexModels?.agentSelected || codexModels?.selected || ""} disabled={modelBusy || !codexModels?.models.length} onChange={(event) => selectModel(event.target.value, "agent")}>
              {!codexModels?.models.length && <option value="">Unavailable</option>}
              {codexModels?.models.map((model) => <option key={model.id} value={model.id}>{model.label}{model.isDefault ? " · default" : ""}</option>)}
            </select>
          </label>
        </div>
        <button className={`takeover ${controlMode === "human" ? "return" : ""}`} onClick={() => setMode(controlMode === "human" ? "ai" : "human")}>
          {controlMode === "human" ? "Return Control to AI" : "Take Control"}
        </button>
        <button className="sign-out" onClick={signOut} title="Sign out of IS4F"><Icon>↪</Icon><span>Sign out</span></button>
      </header>

      {notice && <div className="notice"><span>{notice}</span><button onClick={() => setNotice(undefined)}>×</button></div>}
      {modelNotice && <div className="notice model-notice"><span>{modelNotice}</span><button onClick={() => setModelNotice(undefined)}>×</button></div>}
      {teslaRuntime && status?.services?.scrcpyGateway?.ok === false && <div className="notice"><span>TeslaAndroid touch control is unavailable; automatic recovery is in progress.</span></div>}

      <main className="workspace">
        <section className="device-column">
          <div className="device-meta">
            <div><span className={`dot ${streamState === "streaming" ? "good" : "warn"}`} /> Live device <strong>{status?.device?.screen ? `${status.device.screen.width} × ${status.device.screen.height}` : "—"}</strong></div>
            <div className="package" title={foreground}>{foreground}</div>
          </div>

          <RuntimeStatusPanel device={status?.device} config={streamConfig} backend={streamBackend} />

          <div className={`screen-shell ${isFullscreen ? "fullscreen-active" : ""}`} ref={screenShell}>
            <div ref={deviceFrame} className={`device-frame ${!manualEnabled ? "locked" : ""}`}>
              <div className="speaker" />
              {streamConfigLoaded && status?.device?.screen ? <EmulatorView
                ref={emulator}
                width={dimensions.width}
                height={dimensions.height}
                screenWidth={status?.device?.screen?.width || 1280}
                screenHeight={status?.device?.screen?.height || 720}
                profile={streamProfile}
                backend={streamBackend}
                audioEnabled={audioEnabled}
                fillViewport={isFullscreen}
                onStateChange={(state) => {
                  setStreamState(state);
                  if (state === "streaming") setStreamError(undefined);
                }}
                onAudioStateChange={setAudioState}
                onAudioError={(message) => setNotice(`Audio: ${message}`)}
                onError={handleStreamError}
              /> : <div className="live-screen screen-loading" style={{ width: dimensions.width, height: dimensions.height }}>Connecting to Android…</div>}
              {!manualEnabled && <button type="button" className="control-lock" onClick={() => setMode("human")} title="Take control from AI"><Icon>✦</Icon><strong>AI is controlling Android</strong><span>Take control</span></button>}
            </div>
            {isFullscreen && <button type="button" className="fullscreen-exit" onClick={toggleFullscreen} title="Exit fullscreen" aria-label="Exit fullscreen"><Icon>×</Icon><span>Exit</span></button>}
          </div>

          <div className="device-controls">
            <button onClick={() => emulator.current?.sendKey("GoBack")} title="Back"><Icon>←</Icon><span>Back</span></button>
            <button onClick={() => emulator.current?.sendKey("GoHome")} title="Home"><Icon>○</Icon><span>Home</span></button>
            <button onClick={() => emulator.current?.sendKey("AppSwitch")} title="Recent apps"><Icon>▢</Icon><span>Recent</span></button>
            <span className="control-separator" />
            <button onClick={() => emulator.current?.sendKey("AudioVolumeDown")} title="Volume down"><Icon>◒</Icon></button>
            <button onClick={() => emulator.current?.sendKey("AudioVolumeUp")} title="Volume up"><Icon>◕</Icon></button>
            {streamBackend === "tesla-native" && <button
              className={audioEnabled ? "audio-active" : ""}
              data-audio-state={audioState}
              aria-pressed={audioEnabled}
              onClick={toggleAudio}
              title={audioEnabled ? "Disable TeslaAndroid sound" : "Enable TeslaAndroid sound"}
            ><Icon>{audioEnabled ? "🔊" : "🔇"}</Icon><span>{audioEnabled ? `Sound ${audioState}` : "Sound off"}</span></button>}
            <button onClick={() => streamBackend === "scrcpy-webcodecs" || streamBackend === "remote-waydroid" || streamBackend === "tesla-native" ? emulator.current?.rotate() : manualAction("rotate")} title="Rotate"><Icon>↻</Icon></button>
            <button onClick={downloadScreenshot} title="Screenshot"><Icon>⌑</Icon></button>
            <button onClick={() => { setStreamError(undefined); setStreamState("connecting"); emulator.current?.reconnect(); }} title="Reconnect live screen"><Icon>⟳</Icon></button>
            {teslaRuntime && <button onClick={() => setTeslaSettingsOpen(true)} title="TeslaAndroid device settings"><Icon>⚙</Icon><span>Tesla settings</span></button>}
            <button onClick={toggleFullscreen} title="Fullscreen — fill entire display" aria-pressed={isFullscreen}><Icon>⛶</Icon></button>
          </div>
          {streamError && <div className="stream-error">Stream: {streamError}</div>}
          <PerformanceHud />
        </section>

        <aside className="side-panel">
          <nav className="tabs">
            <button className={tab === "agent" ? "active" : ""} onClick={() => selectTab("agent")}>AI Agent</button>
            <button className={tab === "schedules" ? "active" : ""} onClick={() => selectTab("schedules")}>Schedules</button>
            <button className={tab === "reports" ? "active" : ""} onClick={() => selectTab("reports")}>Reports</button>
            <button className={tab === "expert" ? "active" : ""} onClick={() => selectTab("expert")}>Expert</button>
            <button className={tab === "apps" ? "active" : ""} onClick={() => selectTab("apps")}>Apps</button>
            <button className={tab === "diagnostics" ? "active" : ""} onClick={() => selectTab("diagnostics")}>Diagnostics</button>
          </nav>
          {tab === "agent" && <AgentPanel
            task={task} events={events} messages={messages} actions={actions}
            instruction={instruction} setInstruction={setInstruction} busy={busy}
            onSubmit={submitInstruction} onCommand={taskCommand} onNewSession={newSession}
            onClear={() => { setTask(undefined); setEvents([]); setActions([]); }}
            onOpenGamePlanner={() => selectTab("schedules")}
          />}
          {tab === "schedules" && <SchedulesPanel
            schedules={schedules} runs={scheduleRuns} onChanged={refreshSchedules} onNotice={setNotice}
            onOpenReports={() => selectTab("reports")}
          />}
          {tab === "reports" && <ScheduleReportsPanel
            schedules={schedules} reports={scheduleReports} onRefresh={refreshSchedules}
          />}
          {tab === "expert" && <AndroidExpertPanel
            codexModels={codexModels}
            modelBusy={modelBusy}
            foregroundPackage={status?.foreground?.packageName}
            onSelectModel={selectModel}
            onNotice={setNotice}
          />}
          {tab === "apps" && <AppsPanel onNotice={setNotice} />}
          {tab === "diagnostics" && <Diagnostics status={status} aiHealth={aiHealth} streamState={streamState} streamBackend={streamBackend} controlMode={controlMode} onRefresh={refreshStatus} />}
        </aside>
      </main>
      {teslaSettingsOpen && <TeslaAndroidSettingsDialog
        onClose={() => setTeslaSettingsOpen(false)}
        onNotice={setNotice}
      />}
    </div>
  );
}

function AgentPanel({ task, events, messages, actions, instruction, setInstruction, busy, onSubmit, onCommand, onNewSession, onClear, onOpenGamePlanner }: {
  task?: AgentTask; events: AgentEvent[]; messages: Message[]; actions: AgentAction[]; instruction: string;
  setInstruction: (value: string) => void; busy: boolean; onSubmit: () => void;
  onCommand: (command: "pause" | "resume" | "cancel") => void; onNewSession: () => void; onClear: () => void;
  onOpenGamePlanner: () => void;
}) {
  const active = task && !terminal.has(task.status);
  const androidActions = actions.filter((action) => !observationTools.has(action.action)).length;
  const observations = actions.length - androidActions;
  return <div className="panel-content agent-panel">
    <div className="agent-heading">
      <div><span className="eyebrow">Current task</span><strong>{task?.instruction || "Ready for an instruction"}</strong></div>
      <span className={`task-state state-${task?.status || "idle"}`}>{task?.status || "idle"}</span>
    </div>
    {task?.current_action && <div className="current-action"><span className="event-dot" /><span>Executing</span><strong>{task.current_action}</strong></div>}
    {task && <div className="task-budget" title="Only Android mutations count toward the action limit; screenshots, waits, and UI observations do not.">
      <span><strong>{androidActions}</strong> / {task.max_actions} Android actions</span>
      <span><strong>{observations}</strong> observations</span>
      <span><strong>{Math.round(task.timeout_seconds / 60)}</strong> min time limit</span>
    </div>}
    <div className="conversation">
      {messages.length === 0 && events.length === 0 && <div className="empty-state"><span>✦</span><strong>What should Android do?</strong><p>Try “Open Settings” or ask the agent to inspect an app.</p></div>}
      {messages.slice(-100).map((message) => <div key={`m-${message.id}`} className={`bubble ${message.role}`}><small>{message.role === "user" ? "You" : "IS4F Agent"}</small>{message.content}</div>)}
      {events.slice(-20).map((event) => <div key={`e-${event.id}`} className={`event event-${event.type}`}><span className="event-dot" /><div><small>{new Date(event.createdAt * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</small>{event.message}</div></div>)}
    </div>
    {actions.length > 0 && <details className="timeline"><summary>Tool history <span>{actions.length}</span></summary>{actions.slice(-100).reverse().map((action) => <div key={action.id} className="timeline-row"><span className={action.ok ? "ok" : "failed"}>{action.ok ? "✓" : "!"}</span><div><strong>{action.action}</strong><code>{JSON.stringify(action.arguments)}</code></div><small>{Math.round(action.duration_ms)}ms</small></div>)}</details>}
    <div className="composer">
      <textarea value={instruction} onChange={(event) => setInstruction(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); onSubmit(); } }} placeholder={active ? "Add guidance to the active task…" : "Tell the agent what to do…"} rows={3} />
      <div><small>Enter to send · Shift+Enter for newline</small><button className="send" disabled={busy || !instruction.trim()} onClick={onSubmit}>{active ? "Guide" : "Run task"} <span>↗</span></button></div>
    </div>
    <div className="task-controls">
      <button disabled={!active || task?.status === "paused"} onClick={() => onCommand("pause")}><Icon>Ⅱ</Icon> Pause</button>
      <button disabled={!active || task?.status !== "paused"} onClick={() => onCommand("resume")}><Icon>▶</Icon> Resume</button>
      <button className="danger" disabled={!active} onClick={() => onCommand("cancel")}><Icon>■</Icon> Stop</button>
      <button onClick={onClear}>Clear task</button>
      <button onClick={onNewSession}>New session</button>
      <button className="strategy-link" onClick={onOpenGamePlanner}><Icon>⌕</Icon> Research game strategy</button>
    </div>
  </div>;
}

const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function localDateTimeInput(timestamp = Date.now() / 1000 + 3600) {
  const date = new Date(timestamp * 1000);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

const scheduleFrequencyLabels: Record<AgentSchedule["frequency"], string> = {
  once: "One-time",
  daily: "Daily",
  weekly: "Weekly",
};

function formatScheduleSubject(value: string) {
  const compact = value.trim().replace(/\s+/g, " ");
  if (!compact || compact !== compact.toLowerCase()) return compact;
  const lowerCaseWords = new Set(["a", "an", "and", "at", "for", "in", "of", "on", "the", "to"]);
  return compact.split(" ").map((word, index) => {
    if (index > 0 && lowerCaseWords.has(word)) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(" ");
}

function removeFrequencySuffix(value: string) {
  return value.trim().replace(/\s+(?:daily|weekly|one[- ]time)$/i, "").trim();
}

function scheduleSubject(schedule: AgentSchedule) {
  const suffix = scheduleFrequencyLabels[schedule.frequency];
  if (schedule.kind === "evolution") {
    return formatScheduleSubject(removeFrequencySuffix(schedule.name));
  }
  if (schedule.name.toLowerCase().endsWith(` ${suffix.toLowerCase()}`)) {
    return formatScheduleSubject(removeFrequencySuffix(schedule.name));
  }
  const openedApp = schedule.instruction.match(/^\s*Open\s+(.+?)\s+and\b/i)?.[1];
  return formatScheduleSubject(openedApp || schedule.name);
}

function canonicalScheduleName(subject: string, frequency: AgentSchedule["frequency"]) {
  const base = formatScheduleSubject(removeFrequencySuffix(subject));
  return `${base} ${scheduleFrequencyLabels[frequency]}`.trim();
}

function scheduleDisplayName(schedule: AgentSchedule) {
  return canonicalScheduleName(scheduleSubject(schedule), schedule.frequency);
}

function SchedulesPanel({ schedules, runs, onChanged, onNotice, onOpenReports }: {
  schedules: AgentSchedule[];
  runs: ScheduleRun[];
  onChanged: () => Promise<void>;
  onNotice: (message: string | undefined) => void;
  onOpenReports: () => void;
}) {
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Luxembourg";
  const [editing, setEditing] = useState<string>();
  const [name, setName] = useState("");
  const [taskInstruction, setTaskInstruction] = useState("");
  const [frequency, setFrequency] = useState<AgentSchedule["frequency"]>("daily");
  const [timezone, setTimezone] = useState(browserZone);
  const [timeOfDay, setTimeOfDay] = useState("09:00");
  const [runAt, setRunAt] = useState(localDateTimeInput());
  const [weekdays, setWeekdays] = useState<number[]>([new Date().getDay() === 0 ? 6 : new Date().getDay() - 1]);
  const [maxActions, setMaxActions] = useState(100);
  const [timeoutSeconds, setTimeoutSeconds] = useState(900);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gamePlans, setGamePlans] = useState<GamePlan[]>([]);
  const [evolutionPlans, setEvolutionPlans] = useState<EvolutionPlan[]>([]);
  const [game, setGame] = useState("");
  const [gameGoals, setGameGoals] = useState("Collect daily login rewards, free recurring resources, and other safe daily items efficiently.");
  const [gameRegion, setGameRegion] = useState("");
  const [plannerTimezone, setPlannerTimezone] = useState(browserZone);
  const [plannerTime, setPlannerTime] = useState("09:00");
  const [researching, setResearching] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<string>();
  const scheduleFormRef = useRef<HTMLDivElement>(null);

  const loadGamePlans = useCallback(async () => {
    try {
      const result = await api<{ plans: GamePlan[] }>("/agent/game-plans?limit=20");
      setGamePlans(result.plans);
    } catch (error) {
      onNotice(error instanceof Error ? error.message : String(error));
    }
  }, [onNotice]);

  useEffect(() => { void loadGamePlans(); }, [loadGamePlans]);

  const loadEvolutionPlans = useCallback(async () => {
    try {
      const result = await api<{ plans: EvolutionPlan[] }>("/agent/evolution-plans?limit=100");
      setEvolutionPlans(result.plans);
    } catch (error) {
      onNotice(error instanceof Error ? error.message : String(error));
    }
  }, [onNotice]);

  useEffect(() => { void loadEvolutionPlans(); }, [loadEvolutionPlans]);

  const reset = () => {
    setEditing(undefined); setName(""); setTaskInstruction(""); setFrequency("daily");
    setTimezone(browserZone); setTimeOfDay("09:00"); setRunAt(localDateTimeInput());
    setWeekdays([new Date().getDay() === 0 ? 6 : new Date().getDay() - 1]);
    setMaxActions(100); setTimeoutSeconds(900); setEnabled(true);
  };

  const payload = () => ({
    name: canonicalScheduleName(name, frequency), instruction: taskInstruction.trim(), frequency, timezone,
    timeOfDay: frequency === "once" ? null : timeOfDay,
    runAt: frequency === "once" ? new Date(runAt).toISOString() : null,
    weekdays: frequency === "weekly" ? weekdays : [], enabled,
    maxActions, timeoutSeconds,
  });

  const save = async () => {
    if (!name.trim() || !taskInstruction.trim()) return;
    if (frequency === "weekly" && weekdays.length === 0) return onNotice("Select at least one weekday");
    setSaving(true); onNotice(undefined);
    try {
      if (editing) await api(`/agent/schedules/${editing}`, { method: "PUT", body: JSON.stringify(payload()) });
      else await post("/agent/schedules", payload());
      reset();
      await onChanged();
    } catch (error) {
      onNotice(error instanceof Error ? error.message : String(error));
    } finally { setSaving(false); }
  };

  const edit = (schedule: AgentSchedule) => {
    setEditing(schedule.id); setName(scheduleSubject(schedule)); setTaskInstruction(schedule.instruction);
    setFrequency(schedule.frequency); setTimezone(schedule.timezone);
    setTimeOfDay(schedule.time_of_day || "09:00");
    setRunAt(localDateTimeInput(schedule.run_at)); setWeekdays(schedule.weekdays);
    setMaxActions(schedule.max_actions); setTimeoutSeconds(schedule.timeout_seconds);
    setEnabled(schedule.enabled);
    onNotice(`Editing “${scheduleDisplayName(schedule)}”`);
    window.requestAnimationFrame(() => {
      scheduleFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      scheduleFormRef.current?.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
    });
  };

  const updateEnabled = async (schedule: AgentSchedule, enabled: boolean) => {
    try {
      await api(`/agent/schedules/${schedule.id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: scheduleDisplayName(schedule), instruction: schedule.instruction, frequency: schedule.frequency,
          timezone: schedule.timezone, timeOfDay: schedule.time_of_day || null,
          runAt: schedule.run_at ? new Date(schedule.run_at * 1000).toISOString() : null,
          weekdays: schedule.weekdays, enabled, maxActions: schedule.max_actions,
          timeoutSeconds: schedule.timeout_seconds,
        }),
      });
      await onChanged();
    } catch (error) { onNotice(error instanceof Error ? error.message : String(error)); }
  };

  const runNow = async (schedule: AgentSchedule) => {
    try {
      await post(`/agent/schedules/${schedule.id}/run`);
      onNotice(`Scheduled task “${scheduleDisplayName(schedule)}” queued`);
      await onChanged();
    } catch (error) { onNotice(error instanceof Error ? error.message : String(error)); }
  };

  const remove = async (schedule: AgentSchedule) => {
    if (!window.confirm(`Delete scheduled task “${scheduleDisplayName(schedule)}”?`)) return;
    try {
      await api(`/agent/schedules/${schedule.id}`, { method: "DELETE" });
      if (editing === schedule.id) reset();
      await onChanged();
      await loadEvolutionPlans();
    } catch (error) { onNotice(error instanceof Error ? error.message : String(error)); }
  };

  const describe = (schedule: AgentSchedule) => {
    if (schedule.frequency === "once") return schedule.run_at ? new Date(schedule.run_at * 1000).toLocaleString() : "One time";
    const days = schedule.frequency === "weekly" ? `${schedule.weekdays.map((day) => weekdayLabels[day]).join(", ")} · ` : "Daily · ";
    return `${days}${schedule.time_of_day} · ${schedule.timezone}`;
  };

  const researchGame = async () => {
    if (!game.trim() || !gameGoals.trim()) return;
    setResearching(true); onNotice(undefined);
    try {
      const plan = await post<GamePlan>("/agent/game-plans", {
        game: game.trim(), goals: gameGoals.trim(), region: gameRegion.trim(),
        timezone: plannerTimezone, preferredTime: plannerTime,
      });
      setGamePlans((current) => [plan, ...current.filter((item) => item.id !== plan.id)]);
      setExpandedPlan(plan.id);
      onNotice(`Strategy research for “${plan.game}” is ready. Review a draft before creating its schedule.`);
    } catch (error) {
      onNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setResearching(false);
    }
  };

  const reviewDraft = (gameName: string, draft: GameScheduleDraft) => {
    setEditing(undefined); setName(gameName); setTaskInstruction(draft.instruction);
    setFrequency(draft.frequency); setTimezone(draft.timezone);
    setTimeOfDay(draft.time_of_day); setWeekdays(draft.weekdays);
    setRunAt(localDateTimeInput()); setMaxActions(draft.max_actions);
    setTimeoutSeconds(draft.timeout_seconds); setEnabled(true);
    onNotice("Draft loaded below. Review the instructions and timing, then create the schedule.");
    window.requestAnimationFrame(() => scheduleFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const removeGamePlan = async (plan: GamePlan) => {
    if (!window.confirm(`Delete strategy research for “${plan.game}”?`)) return;
    try {
      await api(`/agent/game-plans/${plan.id}`, { method: "DELETE" });
      setGamePlans((current) => current.filter((item) => item.id !== plan.id));
    } catch (error) {
      onNotice(error instanceof Error ? error.message : String(error));
    }
  };

  return <div className="panel-content schedules-panel">
    <div className="section-title"><div><span className="eyebrow">Autonomous Android jobs</span><h2>Scheduled tasks</h2></div><div className="section-title-actions"><a href="#schedules-report" onClick={(event) => { event.preventDefault(); onOpenReports(); }}>Schedule reports</a><button onClick={onChanged}>Refresh</button></div></div>
    <section className="game-planner">
      <div className="game-planner-heading"><div><span className="planner-mark">⌕</span><div><strong>Game strategy assistant</strong><small>Research current strategies and draft safe collection routines</small></div></div><span className="research-boundary">Review required</span></div>
      <input value={game} onChange={(event) => setGame(event.target.value)} placeholder="Game name, e.g. Clash of Clans" maxLength={120} />
      <textarea value={gameGoals} onChange={(event) => setGameGoals(event.target.value)} rows={3} maxLength={2000} placeholder="What should the assistant research and optimize?" />
      <div className="game-planner-grid">
        <label><span>Region / server</span><input value={gameRegion} onChange={(event) => setGameRegion(event.target.value)} placeholder="Optional, e.g. EU" maxLength={120} /></label>
        <label><span>Preferred time</span><input type="time" value={plannerTime} onChange={(event) => setPlannerTime(event.target.value)} /></label>
        <label className="timezone-field"><span>Timezone</span><input list="is4f-timezones" value={plannerTimezone} onChange={(event) => setPlannerTimezone(event.target.value)} /></label>
      </div>
      <button className="research-game" disabled={researching || !game.trim() || !gameGoals.trim()} onClick={researchGame}>{researching ? "Researching current strategies…" : "Research strategy & draft routine"}</button>
      <small className="planner-disclaimer">Uses isolated read-only web research. It never activates a schedule automatically, and generated routines exclude purchases, premium spending, ads, competitive play, account changes, and anti-bot bypasses.</small>
    </section>
    {gamePlans.length > 0 && <div className="game-plan-list">{gamePlans.map((plan) => <details key={plan.id} className="game-plan" open={expandedPlan === plan.id} onToggle={(event) => setExpandedPlan(event.currentTarget.open ? plan.id : undefined)}>
      <summary><div><strong>{plan.game}</strong><small>Researched {new Date(plan.researched_at * 1000).toLocaleString()} · {plan.schedules.length} draft{plan.schedules.length === 1 ? "" : "s"}</small></div><span>⌄</span></summary>
      <div className="game-plan-body">
        <p className="plan-summary">{plan.summary}</p>
        <div className="plan-columns"><div><strong>Recommended strategy</strong><ul>{plan.strategy_points.map((point, index) => <li key={index}>{point}</li>)}</ul></div>{plan.caveats.length > 0 && <div><strong>Check before automating</strong><ul>{plan.caveats.map((caveat, index) => <li key={index}>{caveat}</li>)}</ul></div>}</div>
        <div className="draft-list">{plan.schedules.map((draft, index) => <article className="game-draft" key={`${plan.id}-${index}`}><div><strong>{canonicalScheduleName(plan.game, draft.frequency)}</strong><small>{draft.frequency === "daily" ? "Daily" : draft.weekdays.map((day) => weekdayLabels[day]).join(", ")} · {draft.time_of_day} · {draft.timezone}</small></div><p>{draft.rationale}</p><button onClick={() => reviewDraft(plan.game, draft)}>Review this schedule</button></article>)}</div>
        <div className="research-sources"><strong>Sources used</strong>{plan.sources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer"><span>{source.title}</span><small>{source.note}</small></a>)}</div>
        <button className="delete-plan" onClick={() => removeGamePlan(plan)}>Delete research</button>
      </div>
    </details>)}</div>}
    <div className={`schedule-form ${editing ? "editing" : ""}`} ref={scheduleFormRef}>
      <div className="schedule-form-heading"><strong>{editing ? "Edit schedule" : "New schedule"}</strong>{editing && <button onClick={reset}>Cancel edit</button>}</div>
      <label className="schedule-name-field"><span>Game or app</span><input aria-label="Game or app" value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Last War" maxLength={100} /></label>
      {name.trim() && <small className="schedule-name-preview">Task label: <strong>{canonicalScheduleName(name, frequency)}</strong></small>}
      <textarea value={taskInstruction} onChange={(event) => setTaskInstruction(event.target.value)} rows={3} placeholder="Open app XYZ, claim the daily reward, verify it succeeded, then complete with a short summary." />
      <div className="schedule-grid">
        <label><span>Frequency</span><select value={frequency} onChange={(event) => setFrequency(event.target.value as AgentSchedule["frequency"])}><option value="once">Once</option><option value="daily">Daily</option><option value="weekly">Selected days</option></select></label>
        {frequency === "once"
          ? <label><span>Run at</span><input type="datetime-local" value={runAt} onChange={(event) => setRunAt(event.target.value)} /></label>
          : <label><span>Time</span><input type="time" value={timeOfDay} onChange={(event) => setTimeOfDay(event.target.value)} /></label>}
        <label className="timezone-field"><span>Timezone</span><input list="is4f-timezones" value={timezone} onChange={(event) => setTimezone(event.target.value)} /><datalist id="is4f-timezones"><option value={browserZone} /><option value="Europe/Luxembourg" /><option value="UTC" /><option value="America/New_York" /><option value="Asia/Tokyo" /></datalist></label>
      </div>
      {frequency === "weekly" && <div className="weekday-picker">{weekdayLabels.map((label, day) => <button key={label} type="button" className={weekdays.includes(day) ? "selected" : ""} onClick={() => setWeekdays((current) => current.includes(day) ? current.filter((item) => item !== day) : [...current, day].sort())}>{label}</button>)}</div>}
      <details className="schedule-limits"><summary>Safety limits</summary><div><label><span>Max actions</span><input type="number" min={1} max={1000} value={maxActions} onChange={(event) => setMaxActions(Number(event.target.value))} /></label><label><span>Timeout (seconds)</span><input type="number" min={30} max={86400} value={timeoutSeconds} onChange={(event) => setTimeoutSeconds(Number(event.target.value))} /></label></div></details>
      <button className="schedule-save" disabled={saving || !name.trim() || !taskInstruction.trim()} onClick={save}>{saving ? "Saving…" : editing ? "Update schedule" : "Create schedule"}</button>
    </div>
    <div className="schedule-list">
      {schedules.length === 0 && <div className="empty-state compact"><strong>No schedules yet</strong><p>Create a one-time or recurring Android task above.</p></div>}
      {schedules.map((schedule) => <details className="schedule-card" key={schedule.id}>
        <summary className="schedule-card-heading"><div><strong>{scheduleDisplayName(schedule)}</strong><small>{describe(schedule)}</small></div><span className={`task-state state-${schedule.last_status || (schedule.enabled ? "queued" : "cancelled")}`}>{schedule.last_status || (schedule.enabled ? "scheduled" : "disabled")}</span><span className="schedule-chevron" aria-hidden="true">⌄</span></summary>
        <div className="schedule-card-body">
          <p>{schedule.instruction}</p>
          <div className="schedule-meta"><span>Next</span><strong>{schedule.next_run_at ? new Date(schedule.next_run_at * 1000).toLocaleString() : "Not scheduled"}</strong>{schedule.last_message && <><span>Last result</span><strong>{schedule.last_message}</strong></>}</div>
          <div className="schedule-actions"><button onClick={() => runNow(schedule)}>Run now</button><button onClick={() => edit(schedule)}>Edit</button><button disabled={schedule.frequency === "once" && Boolean(schedule.last_run_at)} onClick={() => updateEnabled(schedule, !schedule.enabled)}>{schedule.enabled ? "Disable" : "Enable"}</button><button className="danger" onClick={() => remove(schedule)}>Delete</button></div>
          {schedule.frequency === "daily" && schedule.kind !== "evolution" && <EvolutionPlanner
            schedule={schedule}
            gameName={scheduleSubject(schedule)}
            plan={evolutionPlans.find((item) => item.source_schedule_id === schedule.id)}
            onPlansChanged={loadEvolutionPlans}
            onSchedulesChanged={onChanged}
            onNotice={onNotice}
          />}
        </div>
      </details>)}
    </div>
    {runs.length > 0 && <details className="schedule-history"><summary>Recent scheduled runs <span>{runs.length}</span></summary>{runs.slice(-20).reverse().map((run) => { const schedule = schedules.find((item) => item.id === run.schedule_id); return <div key={run.id} className="schedule-run"><span className={`dot ${run.status === "completed" ? "good" : run.status === "failed" || run.status === "cancelled" ? "bad" : "warn"}`} /><div><strong>{schedule ? scheduleDisplayName(schedule) : run.schedule_name}</strong><small>{new Date(run.scheduled_for * 1000).toLocaleString()} · {run.manual ? "manual run" : "scheduled"}</small>{run.message && <p>{run.message}</p>}</div><em>{run.status}</em></div>; })}</details>}
  </div>;
}


function ScheduleReportsPanel({ schedules, reports, onRefresh }: {
  schedules: AgentSchedule[];
  reports: ScheduleRun[];
  onRefresh: () => Promise<void>;
}) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [scheduleFilter, setScheduleFilter] = useState("all");
  const scheduleOptions = Array.from(new Map<string, string>(
    reports.map((report) => {
      const schedule = schedules.find((item) => item.id === report.schedule_id);
      return [
        report.schedule_id,
        schedule ? scheduleDisplayName(schedule) : report.schedule_name,
      ] as [string, string];
    })
  ).entries()).sort((left, right) => left[1].localeCompare(right[1]));
  const visible = reports.filter((report) =>
    (statusFilter === "all" || report.status === statusFilter) &&
    (scheduleFilter === "all" || report.schedule_id === scheduleFilter)
  );
  const duration = (report: ScheduleRun) => {
    if (!report.started_at || !report.finished_at) return "Duration unavailable";
    const seconds = Math.max(0, Math.round(report.finished_at - report.started_at));
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return `${minutes}m ${remainder}s`;
  };

  return <div className="panel-content reports-panel" id="schedules-report">
    <div className="section-title"><div><span className="eyebrow">Persisted terminal history</span><h2>Schedule reports</h2></div><button onClick={onRefresh}>Refresh</button></div>
    <p className="reports-intro">A report is written after every scheduled or manual run reaches completed, failed, or cancelled state. Open a row for the agent result and cleanup outcome.</p>
    <div className="report-filters">
      <label><span>Schedule</span><select value={scheduleFilter} onChange={(event) => setScheduleFilter(event.target.value)}><option value="all">All schedules</option>{scheduleOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label>
      <label><span>Result</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="all">All finished runs</option><option value="completed">Completed</option><option value="failed">Failed</option><option value="cancelled">Cancelled</option></select></label>
    </div>
    <div className="report-summary">
      <span><strong>{visible.length}</strong> shown</span>
      <span><strong>{reports.filter((report) => report.status === "completed").length}</strong> completed</span>
      <span><strong>{reports.filter((report) => report.status === "failed" || report.status === "cancelled").length}</strong> interrupted</span>
    </div>
    <div className="schedule-report-list">
      {visible.length === 0 && <div className="empty-state compact"><strong>No finished reports match these filters</strong><p>Reports appear here only after a scheduled task reaches a terminal state.</p></div>}
      {visible.map((report) => {
        const schedule = schedules.find((item) => item.id === report.schedule_id);
        const label = schedule ? scheduleDisplayName(schedule) : report.schedule_name;
        return <details className="schedule-report" key={report.id}>
          <summary><span className={`dot ${report.status === "completed" ? "good" : "bad"}`} /><div><strong>{label}</strong><small>Finished {new Date((report.finished_at || report.scheduled_for) * 1000).toLocaleString()} · {report.manual ? "manual run" : "scheduled"}</small></div><em>{report.status}</em><span className="schedule-chevron" aria-hidden="true">⌄</span></summary>
          <div className="schedule-report-body"><div className="report-metadata"><span>Scheduled for</span><strong>{new Date(report.scheduled_for * 1000).toLocaleString()}</strong><span>Duration</span><strong>{duration(report)}</strong>{report.task_id && <><span>Task</span><strong>{report.task_id}</strong></>}</div><h3>Agent report</h3><p>{report.message || "The run finished without a report message."}</p></div>
        </details>;
      })}
    </div>
  </div>;
}


function AppsPanel({ onNotice }: { onNotice: (message: string | undefined) => void }) {
  const [apps, setApps] = useState<AppInfo[]>([]);
  const [filter, setFilter] = useState("");
  const [uploading, setUploading] = useState(false);
  const load = useCallback(() => api<{ apps: AppInfo[] }>("/api/apps").then((result) => setApps(result.apps)).catch((error) => onNotice(String(error))), [onNotice]);
  useEffect(() => { load(); }, [load]);
  const action = async (name: string, packageName: string) => {
    if (["uninstall", "clear-app-data"].includes(name) && !window.confirm(`${name === "uninstall" ? "Uninstall" : "Clear all data for"} ${packageName}?`)) return;
    try { await post(`/api/${name}`, { packageName }); await load(); } catch (error) { onNotice(error instanceof Error ? error.message : String(error)); }
  };
  const upload = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData(); form.append("apk", file);
    try { await api("/api/install", { method: "POST", body: form }); await load(); } catch (error) { onNotice(error instanceof Error ? error.message : String(error)); } finally { setUploading(false); }
  };
  const visible = apps.filter((app) => app.packageName.toLowerCase().includes(filter.toLowerCase()));
  return <div className="panel-content apps-panel">
    <div className="section-title"><div><span className="eyebrow">Application management</span><h2>Installed APKs</h2></div><button onClick={load}>Refresh</button></div>
    <label className={`upload ${uploading ? "busy" : ""}`}><span>⬆</span><strong>{uploading ? "Installing APK…" : "Upload and install APK"}</strong><small>Files remain in persistent storage</small><input type="file" accept=".apk,application/vnd.android.package-archive" disabled={uploading} onChange={(event) => upload(event.target.files?.[0])} /></label>
    <input className="search" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Filter package names…" />
    <div className="app-list">{visible.map((app) => <div className="app-row" key={app.packageName}><div className="app-avatar">{app.packageName.split(".").pop()?.slice(0, 2).toUpperCase()}</div><div className="app-name"><strong>{app.packageName}</strong><small>{app.apkPath}</small></div><div className="app-actions"><button onClick={() => action("launch", app.packageName)}>Open</button><button onClick={() => action("stop-app", app.packageName)}>Stop</button><button onClick={() => action("clear-app-data", app.packageName)}>Clear</button><button className="danger" onClick={() => action("uninstall", app.packageName)}>Remove</button></div></div>)}</div>
  </div>;
}

function Diagnostics({ status, aiHealth, streamState, streamBackend, controlMode, onRefresh }: { status?: DeviceStatus; aiHealth?: AiHealth; streamState: string; streamBackend: StreamBackend; controlMode: ControlMode; onRefresh: () => void | Promise<void> }) {
  const [displayRestarting, setDisplayRestarting] = useState(false);
  const [displayRestartResult, setDisplayRestartResult] = useState<string>();
  const [playbackSessions, setPlaybackSessions] = useState<PlaybackTelemetrySession[]>([]);
  const retention = status?.screenshotRetention;
  const teslaRuntime = status?.device?.runtime === "tesla-android-cm4";
  const loadPlaybackSessions = useCallback(async () => {
    try {
      const result = await api<{ sessions: PlaybackTelemetrySession[] }>(
        "/api/playback-telemetry/sessions?limit=10",
      );
      setPlaybackSessions(result.sessions);
    } catch { /* Stack health remains usable while telemetry is starting. */ }
  }, []);
  useEffect(() => { void loadPlaybackSessions(); }, [loadPlaybackSessions]);
  const refreshDiagnostics = async () => {
    await Promise.all([Promise.resolve(onRefresh()), loadPlaybackSessions()]);
  };
  const restartVirtualDisplay = async () => {
    if (!window.confirm("Restart the TeslaAndroid virtual display? The video feed will disconnect briefly and reconnect automatically.")) return;
    setDisplayRestarting(true);
    setDisplayRestartResult("Restarting TeslaAndroid virtual display…");
    try {
      const result = await post<TeslaVirtualDisplayRestartResponse>(
        "/api/tesla-android/virtual-display/restart", { confirm: true },
      );
      setDisplayRestartResult(
        `Virtual display restarted${result.pid ? ` · PID ${result.pid}` : ""} · listener ${result.port} ready`,
      );
      await refreshDiagnostics();
    } catch (error) {
      setDisplayRestartResult(`Virtual display restart failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setDisplayRestarting(false);
    }
  };
  const rows = [
    ["Controller API", Boolean(status)], ["Local Codex AI", Boolean(aiHealth?.ai?.connected)], ["Android / ADB", Boolean(status?.device?.connected)],
    ["Android boot", Boolean(status?.device?.booted)], ["Live screen", streamState === "streaming"],
    ["Native screen gateway", status?.device?.runtimeKind === "physical" || Boolean(status?.services?.webrtcGateway?.ok)], ["scrcpy gateway", Boolean(status?.services?.scrcpyGateway?.ok)], ["Appium", Boolean(status?.services?.appium?.ok)],
    ["Screenshot cleanup", Boolean(retention?.enabled && !retention.lastError)],
  ] as const;
  return <div className="panel-content diagnostics">
    <div className="section-title"><div><span className="eyebrow">Live diagnostics</span><h2>Stack health</h2></div><div className="section-title-actions">{teslaRuntime && <button className="diagnostic-restart" disabled={displayRestarting} onClick={restartVirtualDisplay}>{displayRestarting ? "Restarting display…" : "Restart virtual display"}</button>}<button onClick={refreshDiagnostics}>Refresh</button></div></div>
    {displayRestartResult && <div className="diagnostic-action-result" aria-live="polite">{displayRestartResult}</div>}
    <div className="diagnostic-grid">{rows.map(([label, healthy]) => <div key={label}><span className={`dot ${healthy ? "good" : "bad"}`} /><strong>{label}</strong><small>{healthy ? "Operational" : "Unavailable"}</small></div>)}</div>
    <div className="diagnostic-detail"><label>Android runtime</label><strong>{status?.device?.runtime || "Unknown"}</strong><label>Runtime / node ID</label><strong>{status?.device?.runtimeId || "Local"}</strong><label>Device model</label><strong>{status?.device?.model || "Unknown"}</strong><label>Architecture</label><strong>{status?.device?.architecture || "Unknown"}</strong><label>Graphics renderer</label><strong>{status?.device?.graphicsRenderer || "Unknown"}</strong><label>Graphics recovery</label><strong>{status?.device?.graphicsRecovery?.status || "N/A"} · {status?.device?.graphicsRecovery?.recoveries ?? 0}</strong><label>Encoder</label><strong>{status?.device?.encoder || "Detecting"}</strong><label>Connection</label><strong>{status?.device?.connectionType || "Unknown"}</strong><label>Performance mode</label><strong>{status?.device?.performanceProfile || "Unknown"}</strong><label>Captured / encoded FPS</label><strong>{status?.device?.capturedFps ?? "?"} / {status?.device?.encodedFps ?? "?"}</strong><label>Stream queue / drops</label><strong>{status?.device?.streamQueueDepth ?? "?"} / {status?.device?.streamDrops ?? "?"}</strong><label>ADB secure / ready</label><strong>{status?.device?.adbSecure ?? "?"} / {String(status?.device?.adbReady ?? "?")}</strong><label>Appium</label><strong>{status?.device?.appiumStatus || "Stack service"}</strong><label>Pi temperature</label><strong>{status?.device?.temperatureC === undefined ? "N/A" : `${status.device.temperatureC} °C`}</strong><label>Pi throttling</label><strong>{status?.device?.throttling || "N/A"}</strong><label>Binder warnings</label><strong>{status?.device?.binderWarnings?.count ?? "N/A"}</strong><label>AI authentication</label><strong>{aiHealth?.ai?.authentication || "Unavailable"}</strong><label>Vision model</label><strong>{aiHealth?.ai?.visionModel || "Unknown"}</strong><label>Action model</label><strong>{aiHealth?.ai?.model || "Unknown"}</strong><label>Codex version</label><strong>{aiHealth?.ai?.version || "Unknown"}</strong><label>Control mode</label><strong>{controlMode}</strong><label>Screenshot policy</label><strong>{retention ? `${retention.retentionDays} days · ${(retention.maxBytes / 1024 ** 3).toFixed(0)} GiB max` : "Unknown"}</strong><label>Last screenshot cleanup</label><strong>{retention?.lastRunAt ? `${new Date(retention.lastRunAt * 1000).toLocaleString()} · ${retention.lastResult?.deletedFiles ?? 0} deleted` : "Pending"}</strong><label>Next screenshot cleanup</label><strong>{retention?.nextRunAt ? new Date(retention.nextRunAt * 1000).toLocaleString() : "Pending"}</strong><label>Screen transport</label><strong>{streamState === "streaming" ? (streamBackend === "native-webrtc" ? "Direct native WebRTC" : streamBackend === "remote-waydroid" ? "Pi WDR1 H.264 WebSocket" : streamBackend === "tesla-native" ? "TeslaAndroid native H.264 + touch WebSockets" : "scrcpy H.264 over WebSocket") : streamState}</strong><label>Resolution</label><strong>{status?.device?.screen ? `${status.device.screen.width} × ${status.device.screen.height}` : "Unknown"}</strong></div>
    <details className="playback-history"><summary>Playback telemetry <span>{playbackSessions.length} recent browser sessions</span></summary><div>{playbackSessions.length === 0 && <p>No persisted samples yet. Keep this page open for at least ten seconds.</p>}{playbackSessions.map((session) => <div className="playback-session" key={session.sessionId}><span className={`dot ${session.summary.connectionState === "connected" ? "good" : "warn"}`} /><div><strong>{String(session.summary.foregroundPackage || session.summary.backend || "Browser stream")}</strong><small>{new Date(session.startedAt).toLocaleString()} · {session.sampleCount} samples · {session.lastEvent}</small></div><em>{Number(session.summary.frameGapCount || 0)} video gaps · {Number(session.summary.audioBufferingCount || 0)} audio stalls</em></div>)}</div></details>
    {aiHealth?.ai?.error && <div className="stream-error">AI: {aiHealth.ai.error}</div>}
    <details><summary>Raw status</summary><pre>{JSON.stringify({ android: status, ai: aiHealth }, null, 2)}</pre></details>
  </div>;
}

export default App;
